import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import DashboardPage from './pages/DashboardPage';
import SavedDashboardsPage from './pages/SavedDashboardsPage';
import ViewSavedDashboardPage from './pages/ViewSavedDashboardPage';
import WhatIfAnalysisPage from './pages/WhatIfAnalysisPage';
import LoginPage from './pages/LoginPage';
import ProjectListPage from './pages/ProjectListPage';
import OpportunitiesPage from './pages/OpportunitiesPage';
import AdminSalariesPage from './pages/AdminSalariesPage';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import authService, { User } from './services/authService';
import RoleSelector from './components/RoleSelector';
import ExampleRBACPage from './pages/ExampleRBACPage';

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  
  const updateAccessFlags = (userData: User | null) => {
    const selectedRole = userData?.role || '';
    
    // Show menus based on selected role only
    // Admin menus only when admin role is selected
    // Project owner menus only when project_owner role is selected
    setHasAdminAccess(selectedRole === 'admin');
    setHasProjectAccess(selectedRole === 'project_owner');
  };
  
  useEffect(() => {
    // Get current user
    const userData = authService.getUserData();
    setUser(userData);
    updateAccessFlags(userData);

    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChange((isAuthenticated, userData) => {
      setUser(userData);
      if (isAuthenticated) {
        updateAccessFlags(userData);
      } else {
        updateAccessFlags(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Brand */}
          <div className="flex items-center">
            <Link to="/" className="text-xl font-semibold text-gray-900">
              Project Budgeting and Deal pricing System
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {/* Navigation Toggle Buttons */}
            <nav className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              {/* Project Owner menus */}
              {hasProjectAccess && (
                <>
                  <Link
                    to="/"
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive('/') || isActive('/dashboard')
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Project Budgeting
                  </Link>
                  <Link
                    to="/whatif"
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive('/whatif') || location.pathname.startsWith('/whatif')
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    What-If Analysis
                  </Link>
                  <Link
                    to="/saved-dashboards"
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive('/saved-dashboards') || location.pathname.startsWith('/saved-dashboard/') || location.pathname.startsWith('/opportunity-scenario/')
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Saved Project Analysis
                  </Link>
                  
                </>
              )}
              
              {/* Projects menu - available to both roles */}
              {/* <Link
                to="/projects"
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/projects')
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Projects
              </Link> */}
              
              {/* Admin menus */}
              {hasAdminAccess && (
                <Link
                  to="/admin/salaries"
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive('/admin/salaries')
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Admin
                </Link>
              )}
            </nav>

            {/* User Info & Logout */}
            {user && (
              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="flex flex-col items-end space-y-2">
                  <p className="text-sm font-medium text-gray-900">
                    {user.firstname} {user.lastname}
                  </p>
                  <RoleSelector 
                    onRoleChange={(role) => {
                      // Force re-render after role change
                      window.location.reload();
                    }} 
                  />
                </div>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  title="Logout"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/rbac-example" element={<ExampleRBACPage />} />
        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20">
                <Navigation />
                
                {/* Main Content */}
                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <Routes>
                    {/* Project Owner Routes */}
                    <Route 
                      path="/" 
                      element={
                        <RoleBasedRoute allowedRoles={['project_owner']}>
                          <DashboardPage />
                        </RoleBasedRoute>
                      } 
                    />
                    <Route 
                      path="/dashboard" 
                      element={
                        <RoleBasedRoute allowedRoles={['project_owner']}>
                          <DashboardPage />
                        </RoleBasedRoute>
                      } 
                    />
                    <Route 
                      path="/saved-dashboards" 
                      element={
                        <RoleBasedRoute allowedRoles={['project_owner']}>
                          <SavedDashboardsPage />
                        </RoleBasedRoute>
                      } 
                    />
                    <Route 
                      path="/saved-dashboard/:id" 
                      element={
                        <RoleBasedRoute allowedRoles={['project_owner']}>
                          <ViewSavedDashboardPage />
                        </RoleBasedRoute>
                      } 
                    />
                    <Route 
                      path="/opportunity-scenario/:id" 
                      element={
                        <RoleBasedRoute allowedRoles={['project_owner']}>
                          <ViewSavedDashboardPage kind="opportunity" />
                        </RoleBasedRoute>
                      } 
                    />
                    <Route 
                      path="/opportunities" 
                      element={
                        <RoleBasedRoute allowedRoles={['project_owner']}>
                          <OpportunitiesPage />
                        </RoleBasedRoute>
                      } 
                    />
                    <Route 
                      path="/whatif" 
                      element={
                        <RoleBasedRoute allowedRoles={['project_owner']}>
                          <WhatIfAnalysisPage />
                        </RoleBasedRoute>
                      } 
                    />
                    
                    {/* Admin Routes */}
                    <Route 
                      path="/admin/salaries" 
                      element={
                        <RoleBasedRoute allowedRoles={['admin']}>
                          <AdminSalariesPage />
                        </RoleBasedRoute>
                      } 
                    />
                    
                    {/* Shared Routes (accessible by both roles) */}
                    <Route path="/projects" element={<ProjectListPage />} />
                  </Routes>
                </main>
              </div>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
